import { css } from '@emotion/css'
import { Accordion, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import { VC } from '@semios/app-platform-value-type-definitions'
import { valueGroupEnums } from 'App/Map/PanelDetails/OverviewCharts/valueGroupEnums'
import { IconChevron } from 'components/icons/IconChevron'
import { translate } from 'i18n/i18n'
import { cloneDeep } from 'lodash'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TBlock } from 'stores/fieldAssetStore/helperTypes'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { getCropNameFromId } from 'utils/getCropNameFromId'
import { LayerAccordion } from './LayerAccordion'
import type {
  CombinedDataItem,
  CurrentValuesData,
  FlowRates,
  GroupedData,
  PestSummaryData,
  PropertyInfo,
  PropertyValues,
  RefactoredData,
  TDataItem,
  TDataItemType,
  TDataTransformed,
  TItem,
  TSectionTransformed,
  TValueData,
  TValueGroupEnums,
  TValueGroupLabel,
} from './types'
import { getFavoriteDepths } from './utils'

export const pointsView = ['environment', 'waterManagement', 'windMachine', 'diseases', 'equipment']

export const blocksView = [
  'waterManagement',
  'pests',
  'almondHullSplit',
  'beeHours',
  'chill',
  'diseases',
  'cropDevelopment',
  'sprays',
]

export const comboDataLayers = ['waterManagement', 'diseases']

export const autoSortColumns = [
  'crop',
  'wind',
  'device',
  'pest_type',
  'biofix',
  'root_zone_average',
  'soil_awc_1',
  'soil_awc_2',
  'soil_awc_3',
  'soil_awc_4',
  'lastFetched',
  'irrigationActivity_todaySoFarSum_equipment_ms',
  'irrigationActivity_previous7DaysSum_equipment_ms',
  'irrigationActivity_previous30DaysSum_equipment_ms',
  'irrigationActivity_sinceApr1Sum_equipment_ms',
  'irrigationActivity_sinceJul1Sum_equipment_ms',
  'irrigationActivity_sinceOct1Sum_equipment_ms',
  'irrigationActivity_sinceJan1Sum_equipment_ms',
  'insectDegreeDays_sinceBiofixSumMinMedianMax_insectId',
  'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId',
  'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId',
  'insectTrapCatches_todaySum_insectId',
  'insectTrapCatches_previous2DaysSum_insectId',
  'insectTrapCatches_previous3DaysSum_insectId',
  'insectTrapCatches_previous7DaysSum_insectId',
  'insectTrapCatches_previous14DaysSum_insectId',
  'insectTrapCatches_previous30DaysSum_insectId',
  'insectTrapCatches_previous60DaysSum_insectId',
  'insectTrapCatches_sinceBiofixSum_insectId',
  'insectTrapCatches_sinceJan1Sum_insectId',
  'insectTrapCatches_sinceJul1Sum_insectId',
  ...VC.valuesCurrentValueTypes_Blocks,
  ...VC.valuesCurrentValueTypes_Points,
  ...VC.valuesCurrentValueTypes_Properties,
  ...VC.valuesCurrentValueTypes_IrrigationZoneEmitters,
]

export const SectionTable = ({
  data,
  selectedValueGroupsArray,
  layersActive,
  timestamp,
  isLoading,
}: {
  data: routes.ValuesCurrent.Response
  selectedValueGroupsArray: TValueGroupLabel[]
  layersActive: string[]
  timestamp: string
  isLoading: boolean
}) => {
  const theme = useMantineTheme()
  const [accordionOpened] = useDisclosure(false)

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const allPropertyIds = useMemo(() => Object.keys(properties).map(Number), [properties])

  const flowRates: FlowRates = useMemo(() => {
    const lngLatsIrrigationZones = allPropertyIds.flatMap((propertyId) =>
      Object.values(fieldAssetStore.getState()?.properties?.[Number(propertyId)]?.points ?? []).map(
        (point) => ({
          pointName: point.name,
          propertyId,
          irrigationZones: point.irrigationEmitterZoneIds || [],
        }),
      ),
    )

    const irrigationFlowRates = allPropertyIds
      .flatMap((propertyId) =>
        Object.values(
          fieldAssetStore.getState()?.properties?.[Number(propertyId)]?.irrigationZoneEmitters ?? [],
        ),
      )
      .map((irrigationZone) => ({
        irrigationZone: irrigationZone.irrigationZoneEmitterId,
        flowRate: irrigationZone.flowRate.mmPerHour || 0,
      }))

    const pointsFlowRates = lngLatsIrrigationZones.map((point) => ({
      pointName: point.pointName,
      propertyId: point.propertyId,
      flowRates: point.irrigationZones
        ? point.irrigationZones
            .map((zone) => {
              const rateObj = irrigationFlowRates.find((r) => r.irrigationZone === zone)

              return rateObj ? { irrigationZone: rateObj.irrigationZone, flowRate: rateObj.flowRate } : {}
            })
            .filter(Boolean)
        : [],
    }))

    return { lngLatsIrrigationZones, irrigationFlowRates, pointsFlowRates }
  }, [allPropertyIds])

  const insects = fieldAssetStore.useSelector((s) => s.insects ?? {})
  const { permissions } = userDetailsStore.getState()
  const favoriteDepthsByLngLat = getFavoriteDepths()

  let insectIds: number[] = []

  const processedValueGroups = useMemo(() => {
    selectedValueGroupsArray.forEach((valueGroup) => {
      Object.values(insects).forEach((insect) => {
        const dbName = translate.phrases.dbInsectName(
          insect.name as keyof typeof translate.phrases.dbInsectName,
        )

        if (valueGroup.label === dbName) {
          insectIds.push(insect.insectId)
        }
      })
    })

    return {
      selectedValueGroups: selectedValueGroupsArray
        .filter((sg) => sg.checked !== false)
        .map((sg) => sg.value),
      insectIds,
    }
  }, [selectedValueGroupsArray, insects])

  const trapInsectIds = insectIds
    ? insectIds.filter((id) => permissions[`VIEW_PESTS_TRAP_CATCHES_ID_${id}`])
    : []

  const diseasesSelectedValueGroupsArray = [
    {
      label: translate.phrases.banyanApp('Alternaria'),
      value: 'alternaria',
      checked: processedValueGroups.selectedValueGroups.includes('alternaria'),
    },
    {
      label: translate.phrases.banyanApp('Fire Blight'),
      value: 'fire_blight',
      checked: processedValueGroups.selectedValueGroups.includes('fire_blight'),
    },
  ].filter((selectedValueGroup) => selectedValueGroup.checked !== false)

  const pestsSelectedValueGroupsArray = [
    {
      label: translate.phrases.banyanApp('Trap Catches'),
      value: 'trap_catches',
      checked: true,
    },
    ...selectedValueGroupsArray
      .filter((group) => group.value.startsWith('insect_id_'))
      .map((group) => ({
        label: group.label,
        value: `pest_${group.value.split('_').pop()}`,
        checked: group.checked,
        insectId: group.value.split('_').pop(),
      })),
  ]

  const waterManagementSelectedValueGroupsArray = [
    // NOTE: not yet supported
    // {
    //   label: 'Irrigation Summary',
    //   value: 'irrigation_summary',
    //   checked:
    //     selectedValueGroups.includes('evapotranspiration') ||
    //     selectedValueGroups.includes('irrigation_activity'),
    // },
    {
      label: translate.phrases.banyanApp('Evapotranspiration'),
      value: 'evapotranspiration',
      checked: processedValueGroups.selectedValueGroups.includes('evapotranspiration'),
    },
    {
      label: translate.phrases.banyanApp('Fruit Growth'),
      value: 'fruit_growth',
      checked: processedValueGroups.selectedValueGroups.includes('fruit_growth'),
    },
    {
      label: translate.phrases.banyanApp('Irrigation Activity'),
      value: 'irrigation_activity',
      checked: processedValueGroups.selectedValueGroups.includes('irrigation_activity'),
    },
    {
      label: translate.phrases.banyanApp('Plant Stress'),
      value: 'plant_stress',
      checked: processedValueGroups.selectedValueGroups.includes('plant_stress'),
    },
    {
      label: translate.phrases.banyanApp('Soil'),
      value: 'soil',
      checked: processedValueGroups.selectedValueGroups.includes('soil'),
    },
  ].filter((selectedValueGroup) => selectedValueGroup.checked !== false)

  const processedValueGroupArrays = useMemo(
    () => ({
      pests: pestsSelectedValueGroupsArray,
      waterManagement: waterManagementSelectedValueGroupsArray,
      diseases: diseasesSelectedValueGroupsArray,
    }),
    [
      pestsSelectedValueGroupsArray,
      waterManagementSelectedValueGroupsArray,
      diseasesSelectedValueGroupsArray,
    ],
  )

  const propertiesInfo = useMemo<PropertyInfo[]>(() => {
    const groupedData: GroupedData = {}

    Object.values((properties as Record<string, PropertyValues>) ?? {}).forEach(
      ({ blocks, points, propertyName, timezone }) => {
        if (!blocks || !points) return

        const sortedBlocks = Object.values(blocks)
          .map((block) => ({
            blockId: block.blockId,
            blockName: block.name ?? translate.phrases.banyanApp('Unnamed Block'),
          }))
          .sort(sortByKey('blockName'))

        const sortedPoints = Object.values(points)
          .map((point) => ({ lngLat: point.lngLat, name: point.name }))
          .sort(sortByKey('name'))

        if (!groupedData[propertyName]) {
          groupedData[propertyName] = { blocks: [], points: [], timezone: moment.tz.guess() }
        }

        groupedData[propertyName].blocks.push(...sortedBlocks)

        groupedData[propertyName].points.push(...sortedPoints)

        groupedData[propertyName].timezone = timezone
      },
    )

    return Object.entries(groupedData).map(([propertyName, { blocks, points, timezone }]) => ({
      propertyName,
      blocks: blocks.reduce<Record<string, string>>((acc, { blockId, blockName }) => {
        acc[blockId.toString()] = blockName

        return acc
      }, {}),
      points: points.reduce<Record<string, string>>((acc, { lngLat, name }) => {
        acc[lngLat] = name

        return acc
      }, {}),
      timezone,
    }))
  }, [properties])

  const transformData = (
    valuesGroup: TValueGroupEnums,
    data: {
      blocks?: TDataItem[]
      points?: TDataItem[]
      properties?: TDataItem[]
    },
  ) => {
    let transformed: { [key: string]: unknown } = {}

    for (let key in valuesGroup) {
      if (typeof valuesGroup[key as keyof TValueGroupEnums] === 'object') {
        const nestedTransformed = transformData(
          valuesGroup[key as keyof TValueGroupEnums] as TValueGroupEnums,
          data,
        )

        if (Object.keys(nestedTransformed).length > 0) {
          transformed[key] = nestedTransformed
        }
      } else {
        const additionalKeys = {
          soil: ['root_zone_average', 'height', 'fav_depths'],
          pests: ['crop'],
        }

        if (data.properties && data.properties.some((item) => key in (item.value || {}))) {
          let processedProperties = data.properties
            .map((item) => {
              const newItem: TValueData = {
                propertyName: item.propertyName,
              }

              Object.keys(item.value || {}).forEach((itemValueKey: string) => {
                if (additionalKeys['pests'].includes(itemValueKey)) {
                  newItem[itemValueKey] = item.value?.[itemValueKey] || 0
                } else {
                  newItem[itemValueKey] =
                    (item.value?.[itemValueKey] as VC.ReturnTypeHelpers.TSinglePointValuesReturn<number>)
                      ?.value ||
                    (
                      item.value?.[
                        itemValueKey
                      ] as VC.ReturnTypeHelpers.TMinMedianMaxSpatialValuesReturn<number>
                    )?.median_value ||
                    0
                }
              })

              return Object.keys(newItem).length > 1 ? newItem : {}
            })
            .filter(Boolean)

          if (processedProperties.length > 0) {
            transformed.properties = processedProperties
          }
        }

        if (data.blocks && data.blocks.some((item) => key in (item.value || {}))) {
          let processedBlocks = data.blocks
            .map((item) => {
              const newItem: { [key: string]: unknown } = {
                blockName: item.blockName,
              }

              Object.keys(item.value || {}).forEach((itemValueKey) => {
                if (itemValueKey.startsWith('insectDegreeDays')) {
                  const degreeDaysValue = item.value?.[itemValueKey]

                  if (
                    degreeDaysValue &&
                    typeof degreeDaysValue === 'object' &&
                    'timestamp_from' in degreeDaysValue
                  ) {
                    newItem['biofix'] = degreeDaysValue.timestamp_from
                  }
                }

                if (additionalKeys['pests'].includes(itemValueKey)) {
                  newItem[itemValueKey] = item.value?.[itemValueKey] || 0
                } else {
                  newItem[itemValueKey] =
                    (item.value?.[itemValueKey] as VC.ReturnTypeHelpers.TSinglePointValuesReturn<number>)
                      ?.value ||
                    (
                      item.value?.[
                        itemValueKey
                      ] as VC.ReturnTypeHelpers.TMinMedianMaxSpatialValuesReturn<number>
                    )?.median_value ||
                    0
                }
              })

              return Object.keys(newItem).length > 1 ? newItem : {}
            })
            .filter(Boolean)

          if (processedBlocks.length > 0) {
            transformed.blocks = processedBlocks
          }
        }

        if (data.points && data.points.some((item) => key in (item.value || {}))) {
          let processedPoints = data.points
            .map((item) => {
              const newItem: { [key: string]: unknown } = {
                pointName: item.pointName,
                lngLat: item.lngLat,
              }

              Object.keys(item.value || {}).forEach((itemValueKey) => {
                if (itemValueKey.startsWith('soil_awc_') || additionalKeys['soil'].includes(itemValueKey)) {
                  newItem[itemValueKey] = item.value?.[itemValueKey] || null
                } else {
                  newItem[itemValueKey] =
                    (item.value?.[itemValueKey] as VC.ReturnTypeHelpers.TSinglePointValuesReturn<number>)
                      ?.value || null
                }
              })

              return Object.keys(newItem).length > 1 ? newItem : {}
            })
            .filter(Boolean)

          if (processedPoints.length > 0) {
            transformed.points = processedPoints
          }
        }
      }
    }

    return transformed
  }

  const processComboItems = (
    items: TItem,
    itemType: 'point' | 'block',
    processedSet: Set<string>,
    getItemName: (item: TValueData) => string,
    combinedData: CombinedDataItem[],
  ) => {
    Object.values(items || {}).forEach((itemData) => {
      if (itemData[itemType + 's']) {
        itemData[itemType + 's'].forEach((item) => {
          const itemName = getItemName(item)
          // Check for emitter keys
          const emitterRegex = /_([A-Za-z0-9]+)_ms$/

          const emitterKeys = Object.keys(item).filter(
            (k) => k.startsWith('irrigationActivity_') && k.endsWith('_ms'),
          )

          if (emitterKeys.length > 0) {
            const devices = Array.from(
              new Set(emitterKeys.map((k) => k.match(emitterRegex)?.[1]).filter(Boolean)),
            )

            devices.forEach((device) => {
              // Only include keys relevant to this device/emitter
              const filteredItem: Record<string, unknown> = {}
              const baseKeys = ['pointName', 'blockName', 'type'] // add any non-emitter keys

              baseKeys.forEach((k) => {
                if (item[k] !== undefined) filteredItem[k] = item[k]
              })

              // Add _only_ fields relevant to this emitter
              Object.keys(item).forEach((k) => {
                // Add irrigation fields for this emitter
                if (
                  (k.includes(`_${device}_ms`) && k.startsWith('irrigationActivity_')) ||
                  k === `irrigationIsOn_${device}_status`
                ) {
                  filteredItem[k] = item[k]
                }
              })

              filteredItem['device'] = device

              filteredItem['type'] = itemType

              const id = `${itemName}-${device}`

              if (!processedSet.has(id)) {
                processedSet.add(id)

                combinedData.push(filteredItem as CombinedDataItem)
              }
            })
          }

          // Fallback: just push the item as is
          const keyToCheck = itemType === 'point' ? item.lngLat : itemName

          if (!processedSet.has(keyToCheck as string)) {
            processedSet.add(keyToCheck as string)

            combinedData.push({
              ...item,
              type: itemType,
            })
          }
        })
      }
    })

    return combinedData
  }

  const processItems = (
    data: TDataTransformed,
    property: string,
    sectionKey: string,
    itemType: 'points' | 'blocks' | 'properties',
  ): CombinedDataItem[] => {
    // Only process property-level data for trap catches
    if (sectionKey === 'trap_catches' && itemType !== 'properties') {
      return []
    }

    const groupedItems: Record<string, CombinedDataItem> = {}
    const items: TItem = data[property]?.[sectionKey]?.[itemType] ?? {}

    for (let key in items) {
      const itemArray = items[key]?.[itemType] ?? []
      const nameKey = itemType.slice(0, -1) + 'Name'

      itemArray.forEach((item: TValueData) => {
        const groupKey = item[nameKey] as string

        if (!groupedItems[groupKey]) {
          let newKey = itemType.slice(0, -1)

          if (itemType === 'properties') newKey = 'property'

          groupedItems[groupKey] = { ...item, type: newKey as TDataItemType }
        } else {
          Object.entries(item).forEach(([key, value]) => {
            if (!groupedItems[groupKey][key]) {
              groupedItems[groupKey][key] = value
            }
          })
        }
      })
    }

    return Object.values(groupedItems)
  }

  const createPestSummaryData = (
    combinedData: CombinedDataItem[],
    insectIds: number[],
  ): CombinedDataItem[] => {
    // Group "block" items by crop
    const blocksByCrop: Record<string, CombinedDataItem[]> = {}

    combinedData.forEach((item) => {
      if (item.type === 'block') {
        const crop = (item.crop as string) || ''

        blocksByCrop[crop] ??= []

        blocksByCrop[crop].push(item)
      }
    })

    // Track summary rows for each crop/biofix/insectId
    const summarySet = new Set<string>()
    const summaryItems: CombinedDataItem[] = []

    Object.entries(blocksByCrop).forEach(([crop, blocks]) => {
      // --- Find all unique biofixes in the blocks ---
      // Map all blocks to biofixes, using "" for null/undefined
      let uniqueBiofixes = [...new Set(blocks.map((b) => b.biofix || ''))]

      // Make sure we always have at least one (even if they're all blank)
      if (uniqueBiofixes.length === 0) uniqueBiofixes = ['']

      // --- Group blocks by (biofix, insectId) ---
      type KeyType = `${string}|${string}`

      const pestGroups: Record<KeyType, CombinedDataItem[]> = {}

      blocks.forEach((block) => {
        const degreeKeys = Object.keys(block).filter(
          (k) =>
            k.startsWith('insectDegreeDays_sinceBiofixSumMinMedianMax_insectId') ||
            k.startsWith('insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId') ||
            k.startsWith('insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId'),
        )

        if (degreeKeys.length > 0) {
          degreeKeys.forEach((key) => {
            const insectId = key.match(/insectId(\d+)/)?.[1]
            const biofix = typeof block.biofix === 'string' ? block.biofix : ''
            const groupKey: KeyType = `${biofix}|${insectId}`

            pestGroups[groupKey] ??= []

            pestGroups[groupKey].push(block)
          })
        }
      })

      // --- Create summary rows for data we actually have ---
      Object.entries(pestGroups).forEach(([groupKey, blocks]) => {
        const [biofix, insectId] = groupKey.split('|')
        const summaryKey = `${crop}|${biofix}|${insectId}`

        summarySet.add(summaryKey)

        const getDD = (matchPrefix: string) =>
          blocks
            .map((block) => {
              const key = Object.keys(block).find(
                (k) => k.startsWith(matchPrefix) && k.includes(`insectId${insectId}`),
              )

              return key && typeof block[key] === 'number' ? block[key] : null
            })
            .filter((val) => typeof val === 'number') as number[]

        const currentDDValues = getDD('insectDegreeDays_sinceBiofixSumMinMedianMax_insectId')
        const sevenDayDDValues = getDD('insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax_insectId')
        const fourteenDayDDValues = getDD('insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax_insectId')

        summaryItems.push({
          'type': 'summary',
          crop,
          biofix,
          insectId,
          'currentDD': currentDDValues.length
            ? ({
                low: Math.min(...currentDDValues),
                high: Math.max(...currentDDValues),
              } as keyof PestSummaryData['currentDD'])
            : ({ low: null, high: null } as keyof PestSummaryData['currentDD']),
          '7dayDD': sevenDayDDValues.length
            ? ({
                low: Math.min(...sevenDayDDValues),
                high: Math.max(...sevenDayDDValues),
              } as keyof PestSummaryData['currentDD'])
            : ({ low: null, high: null } as keyof PestSummaryData['currentDD']),
          '14dayDD': fourteenDayDDValues.length
            ? ({
                low: Math.min(...fourteenDayDDValues),
                high: Math.max(...fourteenDayDDValues),
              } as keyof PestSummaryData['currentDD'])
            : ({ low: null, high: null } as keyof PestSummaryData['currentDD']),
        })
      })

      // --- For every [biofix, insectId], add a null summary if needed ---
      uniqueBiofixes.forEach((biofix) => {
        insectIds.forEach((insectId) => {
          const summaryKey = `${crop}|${biofix}|${insectId}`

          if (!summarySet.has(summaryKey)) {
            summaryItems.push({
              'type': 'summary',
              crop,
              biofix,
              'insectId': String(insectId),
              'currentDD': { low: null, high: null } as keyof PestSummaryData['currentDD'],
              '7dayDD': { low: null, high: null } as keyof PestSummaryData['currentDD'],
              '14dayDD': { low: null, high: null } as keyof PestSummaryData['currentDD'],
            })
          }
        })
      })
    })

    return [...combinedData, ...summaryItems]
  }

  const refactorData = (data: TDataTransformed) => {
    let refactored: RefactoredData = {}

    for (let property in data) {
      refactored[property] = {}

      for (let sectionKey in data[property]) {
        let combinedData: CombinedDataItem[] = []

        if (sectionKey === 'pests') {
          // Get trap catches data (property level)
          const trapCatchesData = processItems(data, property, sectionKey, 'properties')

          // Split trap catches by insect ID
          const splitTrapCatchesData = trapCatchesData.flatMap((item) => {
            // Get all unique insect IDs
            const insectIds = [
              ...new Set(
                Object.keys(item)
                  .filter((key) => key.includes('insectTrapCatches_'))
                  .map((key) => key.match(/insectId(\d+)/)?.[1])
                  .filter(Boolean),
              ),
            ]

            // Create a separate record for each insect ID with only its specific data
            return insectIds.map((insectId) => {
              const insectSpecificFields = Object.entries(item).filter(
                ([key]) =>
                  key === 'propertyName' ||
                  key === 'type' ||
                  (key.includes('insectTrapCatches_') && key.endsWith(`insectId${insectId}`)),
              )

              return Object.fromEntries(insectSpecificFields) as CombinedDataItem
            })
          })

          // Get pest models data (block level)
          const pestModelsData = processItems(data, property, sectionKey, 'blocks')

          // Combine all data
          combinedData = [...splitTrapCatchesData, ...pestModelsData]

          // Add pest summary if there's block data
          if (pestModelsData.length > 0) {
            combinedData = createPestSummaryData(combinedData, insectIds)
          }
        } else if (comboDataLayers.includes(sectionKey)) {
          // Create Sets to track unique identifiers
          const processedPoints: Set<string> = new Set()
          const processedBlocks: Set<string> = new Set()

          // Handle data
          if (data[property][sectionKey].points) {
            combinedData = processComboItems(
              data[property][sectionKey].points as TItem,
              'point',
              processedPoints,
              (point) => point.pointName as string,
              combinedData,
            )
          }

          if (data[property][sectionKey].blocks) {
            combinedData = processComboItems(
              data[property][sectionKey].blocks as TItem,
              'block',
              processedBlocks,
              (block) => block.blockName as string,
              combinedData,
            )
          }
        } else {
          // Process data with duplicate checking
          combinedData.push(
            ...processItems(data, property, sectionKey, 'points'),
            ...processItems(data, property, sectionKey, 'blocks'),
            ...processItems(data, property, sectionKey, 'properties'),
          )
        }

        refactored[property][sectionKey] = combinedData
      }
    }

    return refactored
  }

  const transformedData = useMemo(() => {
    const clonedData = cloneDeep(data)

    if (
      layersActive.includes('Pests') ||
      processedValueGroups.selectedValueGroups.includes('evapotranspiration') ||
      processedValueGroups.selectedValueGroups.includes('soil')
    ) {
      const allBlocks = allPropertyIds
        .flatMap((propertyId) =>
          Object.values(fieldAssetStore.getState()?.properties?.[Number(propertyId)]?.blocks ?? []),
        )
        .reduce((acc: { [key: string]: TBlock }, block: TBlock) => {
          acc[block.blockId] = block

          return acc
        }, {})

      if (layersActive.includes('Pests')) {
        if (clonedData?.blocks) {
          clonedData.blocks = Object.fromEntries(
            Object.entries(clonedData.blocks).map(([blockId, block]) => [
              blockId,
              {
                ...block,
                crop: getCropNameFromId(allBlocks[blockId]?.cropIds?.[0]),
              },
            ]),
          )
        }
      }

      // add crop for evapotranspiration
      if (processedValueGroups.selectedValueGroups.includes('evapotranspiration')) {
        if (clonedData?.blocks) {
          clonedData.blocks = Object.fromEntries(
            Object.entries(clonedData.blocks).map(([blockId, block]) => [
              blockId,
              {
                ...block,
                crop: getCropNameFromId(allBlocks[blockId]?.cropIds?.[0]),
              },
            ]),
          )
        }
      }

      // add soil depths
      if (processedValueGroups.selectedValueGroups.includes('soil')) {
        if (clonedData.points !== undefined) {
          const points = clonedData.points

          Object.entries(points).forEach(([lngLat, pointObj]) => {
            const rawDepths = pointObj.soilAWC_eachDepth_pct?.value || {}

            const allDepths: Record<string, number> = Object.fromEntries(
              Object.entries(rawDepths)
                .filter(([, v]) => v !== null && v !== undefined)
                .map(([k, v]) => [k, v as number]),
            )

            const favDepths = favoriteDepthsByLngLat[lngLat] ?? []

            if (favDepths.length) {
              const favDepthKeys = favDepths.map((d: string) =>
                typeof d === 'string' ? d.replace('cm', '') : String(d),
              )

              const favDepthValues = favDepthKeys
                .map((key: string) => allDepths[key])
                .filter((v: number | undefined) => typeof v === 'number')

              const rootZoneAverage =
                favDepthValues.length > 0
                  ? favDepthValues.reduce((sum, val) => sum + val, 0) / favDepthValues.length
                  : 0

              const favDepthKeyValuePairs = favDepthValues.map((val, idx) => ({
                [`soil_awc_${idx + 1}`]: val ?? 0,
              }))

              points[lngLat] = {
                ...pointObj,
                root_zone_average: rootZoneAverage,
                fav_depths: favDepths,
                ...favDepthKeyValuePairs.reduce((acc, obj) => ({ ...acc, ...obj }), {}),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
          })
        }
      }
    }

    const newDataObj: CurrentValuesData = {}

    Object.keys(clonedData).forEach((keyName) => {
      const keyValue = clonedData[keyName as keyof typeof clonedData]

      if (keyName === 'blocks' && keyValue) {
        Object.entries(keyValue).forEach(([blockId, value]) => {
          const propertyInfo = propertiesInfo.find((property) => property.blocks[blockId])
          const propertyName = propertyInfo?.propertyName
          const blockName = propertyInfo?.blocks[blockId]
          const timezone = propertyInfo?.timezone

          if (propertyName) {
            if (!newDataObj[propertyName]) {
              newDataObj[propertyName] = { blocks: [], points: [], properties: [] }
            }

            newDataObj[propertyName].blocks?.push({ blockName, value, timezone })
          }
        })
      }

      if (keyName === 'points' && keyValue) {
        Object.entries(keyValue).forEach(([lngLat, value]) => {
          const propertyInfo = propertiesInfo.find((property) => property.points[lngLat])
          const propertyName = propertyInfo?.propertyName
          const pointName = propertyInfo?.points[lngLat]
          const timezone = propertyInfo?.timezone

          if (propertyName) {
            if (!newDataObj[propertyName]) {
              newDataObj[propertyName] = { blocks: [], points: [], properties: [] }
            }

            newDataObj[propertyName].points?.push({ pointName, lngLat, value, timezone })
          }
        })
      }

      if (keyName === 'properties' && keyValue) {
        Object.entries(keyValue).forEach(([propertyId, value]) => {
          const propertyInfo = properties[Number(propertyId)]
          const propertyName = propertyInfo?.propertyName
          const timezone = propertyInfo?.timezone

          if (propertyName) {
            if (!newDataObj[propertyName]) {
              newDataObj[propertyName] = { blocks: [], points: [], properties: [] }
            }

            newDataObj[propertyName].properties?.push({ propertyName, value, timezone })
          }
        })
      }
    })

    let result: TDataTransformed = {}

    for (let property in newDataObj) {
      result[property] = transformData(
        valueGroupEnums(processedValueGroups.insectIds, trapInsectIds),
        newDataObj[property],
      ) as TSectionTransformed
    }

    return refactorData(result)
  }, [
    data,
    processedValueGroups,
    properties,
    propertiesInfo,
    selectedValueGroupsArray,
    layersActive,
    timestamp,
  ])

  // keep track of open layers to always have the top layer open by default
  const [openLayers, setOpenLayers] = useState<string[]>(layersActive.length ? [layersActive[0]] : [])

  useEffect(() => {
    // If there are layers
    if (layersActive.length > 0) {
      // If top layer is not currently open, force it open
      if (!openLayers.includes(layersActive[0])) {
        setOpenLayers([layersActive[0]])
      } else {
        // Filter openLayers to match current layers
        const validOpenLayers = openLayers.filter((l) => layersActive.includes(l))

        if (
          validOpenLayers.length !== openLayers.length ||
          !validOpenLayers.every((v, i) => v === openLayers[i])
        ) {
          setOpenLayers(validOpenLayers)
        }
      }
    } else {
      setOpenLayers([])
    }
  }, [layersActive])

  if (selectedValueGroupsArray.length === 0) {
    return (
      <div css={{ padding: '20px', color: '#ababab' }}>{translate.phrases.banyanApp('Select a value')}</div>
    )
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        opacity: 1,
        height: 'auto',
        transition: '200ms ease all',
        overflow: 'unset',
        boxSizing: 'border-box',
        border: `0.5px solid ${theme.colors.grey[2]}`,
        borderCollapse: 'collapse',
        overflowX: 'auto',
      })}
    >
      <Accordion
        styles={{
          content: { padding: 0 },
          label: {
            fontSize: 16,
            fontWeight: 700,
            color: colors.midnight,
          },
        }}
        chevron={
          <div
            css={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 15,
            }}
          >
            <div
              css={{
                transform: accordionOpened ? 'rotate(180deg)' : undefined,
              }}
            >
              <IconChevron />
            </div>
          </div>
        }
        multiple
        value={openLayers}
        onChange={setOpenLayers}
        chevronPosition="left"
      >
        {layersActive.map((layer, idx) => (
          <LayerAccordion
            key={`${layer}-${idx}`}
            sectionIndex={idx}
            layer={layer}
            transformedData={transformedData}
            processedValueGroupArrays={processedValueGroupArrays}
            selectedValueGroupsArray={selectedValueGroupsArray}
            timestamp={timestamp}
            insectIds={processedValueGroups.insectIds}
            flowRates={flowRates.pointsFlowRates}
            isLoading={isLoading}
            totalLayers={layersActive.length}
          />
        ))}
      </Accordion>
    </div>
  )
}
